import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { PackageCheck, Plus } from "lucide-react";

export default function Packing() {
  const [packs, setPacks] = useState([]);
  const [picks, setPicks] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ pick_id: "", carton_count: 1, weight_kg: 0, dimensions_cm: "", notes: "" });

  const load = async () => {
    const [p, pk] = await Promise.all([api.get("/warehouse/packs"), api.get("/warehouse/picks")]);
    setPacks(p.data); setPicks(pk.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post("/warehouse/packs", form);
      toast.success("Pack created with barcode");
      setOpen(false); setForm({ pick_id: "", carton_count: 1, weight_kg: 0, dimensions_cm: "", notes: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const completedPicks = picks.filter((p) => p.status === "COMPLETED");

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="warehouse-packing">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black flex items-center gap-2">
            <PackageCheck className="w-7 h-7 text-primary" /> Packing
          </h1>
          <p className="text-muted-foreground">Carton packing, weight/dim capture & barcode generation.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="pack-new" disabled={completedPicks.length === 0}>
          <Plus className="w-4 h-4 mr-1" /> New pack
        </Button>
      </div>

      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Pack #</TableHead><TableHead>From pick</TableHead><TableHead>Order ref</TableHead>
            <TableHead className="text-right">Cartons</TableHead><TableHead className="text-right">Weight (kg)</TableHead>
            <TableHead>Dims</TableHead><TableHead>Barcode</TableHead><TableHead>Status</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {packs.map((p) => (
              <TableRow key={p.id} data-testid={`pack-row-${p.number}`}>
                <TableCell className="font-mono text-xs">{p.number}</TableCell>
                <TableCell className="font-mono text-xs">{p.pick_number}</TableCell>
                <TableCell className="font-mono text-xs">{p.order_ref || "—"}</TableCell>
                <TableCell className="text-right">{p.carton_count}</TableCell>
                <TableCell className="text-right">{p.weight_kg}</TableCell>
                <TableCell className="text-xs">{p.dimensions_cm || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{p.barcode}</TableCell>
                <TableCell><Badge variant={p.status === "DISPATCHED" ? "default" : "outline"}>{p.status}</Badge></TableCell>
              </TableRow>
            ))}
            {packs.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-10">No packs yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New pack</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>From pick (completed only)</Label>
              <Select value={form.pick_id} onValueChange={(v) => setForm({ ...form, pick_id: v })}>
                <SelectTrigger data-testid="pack-pick"><SelectValue placeholder="Select completed pick…" /></SelectTrigger>
                <SelectContent>{completedPicks.map((p) => <SelectItem key={p.id} value={p.id}>{p.number} · {p.customer_name || p.order_ref || "—"}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Cartons</Label><Input type="number" value={form.carton_count} onChange={(e) => setForm({ ...form, carton_count: parseInt(e.target.value || "1") })} data-testid="pack-cartons" /></div>
              <div><Label>Weight (kg)</Label><Input type="number" value={form.weight_kg} onChange={(e) => setForm({ ...form, weight_kg: parseFloat(e.target.value || "0") })} data-testid="pack-weight" /></div>
              <div><Label>Dimensions</Label><Input placeholder="L x W x H cm" value={form.dimensions_cm} onChange={(e) => setForm({ ...form, dimensions_cm: e.target.value })} data-testid="pack-dim" /></div>
            </div>
            <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={!form.pick_id} data-testid="pack-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
